import React, { useState, useEffect } from 'react';
import HeaderOne from "../components/HeaderTwo";
import BottomFooter from "../components/BottomFooter";

const FAQ = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeIndex, setActiveIndex] = useState(null);
    const [faqData, setFaqData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchFAQs();
    }, []);

    const fetchFAQs = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/faq/`);
            
            if (!response.ok) {
                throw new Error('Failed to fetch FAQs');
            }
            
            const data = await response.json();
            
            // Transform API data to match your component structure
            const transformedData = data.map(category => ({
                category: category.name,
                questions: category.faqs.map(faq => ({
                    question: faq.question,
                    answer: faq.answer
                }))
            }));
            
            setFaqData(transformedData);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
            console.error('Error fetching FAQs:', err);
        }
    };

    // Flatten all questions for search
    const allQuestions = faqData.flatMap((category, catIndex) =>
        category.questions.map((q, qIndex) => ({
            ...q,
            category: category.category,
            catIndex,
            qIndex
        }))
    );

    // Filter questions based on search
    const filteredQuestions = searchTerm
        ? allQuestions.filter(
              (item) =>
                  item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  item.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  item.category.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : allQuestions;

    const toggleAccordion = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    if (loading) {
        return (
            <>
                <HeaderOne />
                <div className="faq-container">
                    <section className="faq-hero-section">
                        <div className="hero-content">
                            <h1 className="hero-title">Frequently Asked Questions</h1>
                            <p className="hero-subtitle">Loading FAQs...</p>
                        </div>
                    </section>
                </div>
                <BottomFooter />
            </>
        );
    }

    if (error) {
        return (
            <>
                <HeaderOne />
                <div className="faq-container">
                    <section className="faq-hero-section">
                        <div className="hero-content">
                            <h1 className="hero-title">Frequently Asked Questions</h1>
                            <p className="hero-subtitle" style={{ color: 'red' }}>
                                Error loading FAQs: {error}
                            </p>
                        </div>
                    </section>
                </div>
                <BottomFooter />
            </>
        );
    }

    return (
        <>
            <HeaderOne />

            <div className="faq-container">
                {/* Hero Section */}
                <section className="faq-hero-section">
                    <div className="hero-content">
                        <h1 className="hero-title">Frequently Asked Questions</h1>
                        <p className="hero-subtitle">
                            Find answers to common questions about our services
                        </p>
                    </div>
                </section>

                {/* Main Content */}
                <section className="faq-main-content">
                    <div className="content-wrapper">
                        {/* Search Bar */}
                        <div className="faq-search-section">
                            <div className="search-container">
                                <span className="search-icon">🔍</span>
                                <input
                                    type="text"
                                    placeholder="Search for answers..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="search-input"
                                />
                                {searchTerm && (
                                    <button
                                        className="clear-search"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                            {searchTerm && (
                                <p className="search-results-text">
                                    Found {filteredQuestions.length} result{filteredQuestions.length !== 1 ? 's' : ''}
                                </p>
                            )}
                        </div>

                        {/* FAQ Content */}
                        {!searchTerm ? (
                            <div className="faq-categories">
                                {faqData.map((category, catIndex) => (
                                    <div key={catIndex} className="faq-category">
                                        <h2 className="category-title">{category.category}</h2>
                                        <div className="questions-list">
                                            {category.questions.map((item, qIndex) => {
                                                const uniqueIndex = `${catIndex}-${qIndex}`;
                                                const isActive = activeIndex === uniqueIndex;

                                                return (
                                                    <div
                                                        key={qIndex}
                                                        className={`faq-item ${isActive ? 'active' : ''}`}
                                                    >
                                                        <button
                                                            className="faq-question"
                                                            onClick={() => toggleAccordion(uniqueIndex)}
                                                        >
                                                            <span className="question-text">{item.question}</span>
                                                            <span className="toggle-icon">
                                                                {isActive ? '−' : '+'}
                                                            </span>
                                                        </button>
                                                        <div className={`faq-answer ${isActive ? 'show' : ''}`}>
                                                            <p>{item.answer}</p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="faq-search-results">
                                {filteredQuestions.length > 0 ? (
                                    <div className="questions-list">
                                        {filteredQuestions.map((item, index) => {
                                            const isActive = activeIndex === index;

                                            return (
                                                <div
                                                    key={index}
                                                    className={`faq-item ${isActive ? 'active' : ''}`}
                                                >
                                                    <span className="result-category">{item.category}</span>
                                                    <button
                                                        className="faq-question"
                                                        onClick={() => toggleAccordion(index)}
                                                    >
                                                        <span className="question-text">{item.question}</span>
                                                        <span className="toggle-icon">
                                                            {isActive ? '−' : '+'}
                                                        </span>
                                                    </button>
                                                    <div className={`faq-answer ${isActive ? 'show' : ''}`}>
                                                        <p>{item.answer}</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="no-results">
                                        <p className="no-results-icon">🔍</p>
                                        <h3>No results found</h3>
                                        <p>Try different keywords or browse our categories above</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Contact CTA */}
                        <div className="faq-cta-section">
                            <h2>Still Have Questions?</h2>
                            <p>
                                Can't find what you're looking for? Our team is here to help!
                            </p>
                            <div className="cta-buttons">
                                <a href="tel:9023652806" className="cta-button">
                                    📞 Call Us: (902) 365-2806
                                </a>
                                <a href="mailto:info@pandaprint.ca" className="cta-button secondary">
                                    ✉️ Email Us
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            <BottomFooter />
        </>
    );
};

export default FAQ;
